import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  NAV_SEGMENT_LABEL_BTN,
  NAV_SEGMENT_ROOT,
  navSegmentActive,
  navSegmentIdle,
  navSegmentShell,
} from '../layout/navSegmentSwitchStyles';

/** Route-scoped localStorage keys for per-page Hindi/English preference. */
export const JYOTISHA_LANG_STORAGE_KEYS = {
  parashariLearn: 'parashari-learn-lang',
  sadhana: 'sadhana-lang',
  learnerAccount: 'learner-account-lang',
  ai: 'ai-lang',
};

const JyotishaLanguageContext = createContext({
  showEnglish: true,
  setShowEnglish: () => {},
});

function readStoredShowEnglish(storageKey) {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

/**
 * @param {{
 *   children: React.ReactNode;
 *   storageKey?: string;
 *   persist?: boolean;
 *   defaultShowEnglish?: boolean;
 * }} props
 */
export function JyotishaLanguageProvider({
  children,
  storageKey = JYOTISHA_LANG_STORAGE_KEYS.parashariLearn,
  persist = true,
  defaultShowEnglish = true,
}) {
  const [showEnglish, setShowEnglish] = useState(() =>
    persist ? readStoredShowEnglish(storageKey) : defaultShowEnglish,
  );

  useEffect(() => {
    if (!persist || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, showEnglish ? 'true' : 'false');
    } catch {
      // ignore storage failures
    }
  }, [showEnglish, storageKey, persist]);

  const value = useMemo(() => ({ showEnglish, setShowEnglish }), [showEnglish]);

  return <JyotishaLanguageContext.Provider value={value}>{children}</JyotishaLanguageContext.Provider>;
}

export function useJyotishaLanguage() {
  return useContext(JyotishaLanguageContext);
}

export function JyotishaLanguageNavSwitch({ isDark, className = '' }) {
  const { showEnglish, setShowEnglish } = useJyotishaLanguage();

  return (
    <div
      className={`${NAV_SEGMENT_ROOT} ${navSegmentShell(isDark)} ${className}`}
      role="group"
      aria-label="Language"
    >
      <button
        type="button"
        onClick={() => setShowEnglish(false)}
        aria-pressed={!showEnglish}
        aria-label="Hindi"
        title="Hindi"
        className={`${NAV_SEGMENT_LABEL_BTN} ${!showEnglish ? navSegmentActive(isDark) : navSegmentIdle(isDark)}`}
      >
        <span className="font-devanagari" lang="hi">
          हिं
        </span>
      </button>
      <button
        type="button"
        onClick={() => setShowEnglish(true)}
        aria-pressed={showEnglish}
        aria-label="English"
        title="English"
        className={`${NAV_SEGMENT_LABEL_BTN} ${showEnglish ? navSegmentActive(isDark) : navSegmentIdle(isDark)}`}
      >
        En
      </button>
    </div>
  );
}

/** @deprecated Use JyotishaLanguageNavSwitch */
export function JyotishaEnglishToggle({ isDark, className = '' }) {
  return <JyotishaLanguageNavSwitch isDark={isDark} className={className} />;
}
